import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import { MySelect } from "../../components/ui/Select";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import { cn } from "../../lib/utils";
import OngoingTripDropdownMenu from "../dispatch/components/OngoingTripsDropdown";
import DriverDropdownMenu from "../driver/ui/DriverDropdownMenu";
import VehicleDropdownMenu from "../vehicle/ui/VehicleDropdownMenu";
import { IncidentReport } from "./@types";
import {
  useAddIncidentReportMutation,
  useUpdateIncidentReportMutation,
} from "./incidentReportApiSlice";

export const maintenanceFormSchema = z.object({
  tripId: z.string().nonempty("Trip ID is required"),
  reportType: z.string().nonempty("Report type is required"),
  description: z.string().nonempty("Description is required"),
  location: z.string().nonempty("Location is required"),
  severity: z.string().nonempty("Severity is required"),
  driverId: z.string().nonempty("Driver ID is required"),
  vehicleId: z.string().nonempty("Vehicle ID is required"),
});

export type IncidentReportFormData = z.infer<typeof maintenanceFormSchema>;

interface IncidentReportModelProps {
  isOpen: boolean;
  onClose: () => void;
  incidentReport?: IncidentReport | null;
  mode?: "add" | "edit";
}

const IncidentReportModal: React.FC<IncidentReportModelProps> = ({
  isOpen,
  onClose,
  incidentReport = null,
  mode = incidentReport ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && incidentReport;

  const [
    createIR,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAddIncidentReportMutation();

  const [
    updateIR,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateIncidentReportMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<IncidentReportFormData>({
    resolver: zodResolver(maintenanceFormSchema),
    mode: "onChange",
    defaultValues: {
      driverId: "",
      vehicleId: "",
      location: "",
      reportType: "",
      description: "",
      severity: "",
    },
  });

  // Populate form with incidentReport data when editing
  useEffect(() => {
    if (isEditMode && incidentReport) {
      reset({
        tripId: incidentReport.tripId,
        reportType: incidentReport.reportType,
        description: incidentReport.description,
        location: incidentReport.location,
        severity: incidentReport.severity,
        driverId: incidentReport.driverId,
        vehicleId: incidentReport.vehicleId,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        tripId: "",
        reportType: "",
        description: "",
        location: "",
        severity: "",
        driverId: "",
        vehicleId: "",
      });
    }
  }, [isEditMode, incidentReport, reset]);

  const resetMutation = () => {
    resetCreate();
    resetUpdate();
  };

  const onSubmit = async (data: IncidentReportFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        tripId: data.tripId,
        reportType: data.reportType,
        description: data.description,
        location: data.location,
        severity: data.severity,
        driverId: data.driverId,
        vehicleId: data.vehicleId,
      };

      if (isEditMode && incidentReport?.id) {
        await updateIR({
          id: incidentReport.id,
          ...payload,
        }).unwrap();
      } else {
        await createIR(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } incident report. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Incident report ${
          isEditMode ? "updated" : "added"
        } successfully`,
      });
      setShowSuccess(true);
      resetMutation();
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } Incident report. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
      resetMutation();
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Incident report has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Add"} incident report`;
  const submitButtonText = isEditMode
    ? "Update incident report"
    : "+ Add incident report";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-4"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-0 p-6">
          <div className="">
            <Controller
              control={control}
              name="tripId"
              render={({ field }) => (
                <OngoingTripDropdownMenu
                  value={watch("tripId")}
                  onChange={(value) =>
                    setValue("tripId", value, {
                      shouldValidate: true,
                    })
                  }
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.driverId?.message}
                  placeholder="Select on going trip"
                />
              )}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="reportType"
              render={({ field }) => (
                <MySelect<string>
                  name={"name"}
                  id={"id"}
                  label={"Report type"}
                  options={["Incident", "Accident"]}
                  getValue={(option: string) => option}
                  getName={(option: string) => option}
                  value={field.value}
                  onChange={(e: any) => setValue("reportType", e ?? "")}
                  onValChange={(e: any) => setValue("reportType", e ?? "")}
                  placeholder={"Report type"}
                  disabled={isLoading}
                  className={cn(
                    "focus:ring-primary focus:border-primary",
                    "text-[13px] px-2 text-textColor appearance-none bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  )}
                />
              )}
            />

            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <AppInputField
                  label="Description"
                  type="text"
                  error={errors.description?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter description"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="location"
              render={({ field }) => (
                <AppInputField
                  label="Location"
                  type="text"
                  error={errors.description?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  placeholder="Enter location"
                />
              )}
            />

            <Controller
              control={control}
              name="severity"
              render={({ field }) => (
                <MySelect<string>
                  name={"name"}
                  id={"id"}
                  label={"Severity"}
                  options={["Minor", "Bad", "Critical"]}
                  getValue={(option: string) => option}
                  getName={(option: string) => option}
                  value={field.value}
                  onChange={(e: any) => setValue("severity", e)}
                  onValChange={(e: any) => setValue("severity", e)}
                  placeholder={"Severity"}
                  disabled={isLoading}
                  className={cn(
                    "focus:ring-primary focus:border-primary",
                    "text-[13px] px-2 text-textColor appearance-none bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  )}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="vehicleId"
              render={({ field }) => (
                <VehicleDropdownMenu
                  value={field.value}
                  onChange={(value) =>
                    setValue("vehicleId", value, {
                      shouldValidate: true,
                    })
                  }
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.vehicleId?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="driverId"
              render={({ field }) => (
                <DriverDropdownMenu
                  value={field.value}
                  onChange={(value) =>
                    setValue("driverId", value, {
                      shouldValidate: true,
                    })
                  }
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                  error={errors.driverId?.message}
                  placeholder="Select a driver"
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default IncidentReportModal;
